import { formatDateLabel, toDateInputValue } from "../../../utils/dateOnly";

// Regras da tela Recalcular frete, separadas da casca visual na sprint 638
// (padrão do Hub). Copiadas sem mudança de index.js: tipos de execução, status,
// atalhos de período, formatação do log e o montador da requisição
// (endpoints e corpo enviados ao backend continuam os mesmos).

export const TIPO_NF = "NF";
export const TIPO_CTE = "CTE";
export const TIPO_ARQUIVO = "ARQUIVO";
export const ASYNC_CTE_THRESHOLD = 1000;

export const STATUS_CALCULO_OPTIONS = [
  { value: "nunca_calculado", label: "Nunca calculado", statuses: [0] },
  { value: "pendente_recalculo", label: "Pendente de recálculo", statuses: [2, 4] },
  { value: "frete_calculado", label: "Frete calculado", statuses: [1, 3] },
];

export const DEFAULT_STATUS_CALCULO = ["nunca_calculado", "pendente_recalculo"];
export const ALL_STATUS_CALCULO = STATUS_CALCULO_OPTIONS.map((option) => option.value);

export const SITUACAO_CTE_OPTIONS = [
  { value: 1, label: "A Validar" },
  { value: 2, label: "OK" },
  { value: 3, label: "Não OK" },
];

function toDateInput(date) {
  return toDateInputValue(date);
}

export const DATE_SHORTCUTS = [
  {
    label: "7 dias",
    getDates: () => {
      const hoje = new Date();
      const de = new Date();
      de.setDate(hoje.getDate() - 6);
      return { de: toDateInput(de), ate: toDateInput(hoje) };
    },
  },
  {
    label: "30 dias",
    getDates: () => {
      const hoje = new Date();
      const de = new Date();
      de.setDate(hoje.getDate() - 29);
      return { de: toDateInput(de), ate: toDateInput(hoje) };
    },
  },
  {
    label: "Mês atual",
    getDates: () => {
      const hoje = new Date();
      const de = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const ate = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      return { de: toDateInput(de), ate: toDateInput(ate) };
    },
  },
  {
    label: "Mês anterior",
    getDates: () => {
      const hoje = new Date();
      const de = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
      const ate = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
      return { de: toDateInput(de), ate: toDateInput(ate) };
    },
  },
];

export function formatRouteLabel(origem, destino) {
  if (!origem && !destino) return null;
  if (!origem) return String(destino).toUpperCase();
  if (!destino) return String(origem).toUpperCase();
  return `${String(origem).toUpperCase()} > ${String(destino).toUpperCase()}`;
}

export function formatCepLabel(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return null;
  const cep = digits.padStart(8, "0").slice(-8);
  return cep.replace(/(\d{5})(\d{3})/, "$1-$2");
}

export function formatPesoLabel(value) {
  if (value == null || value === "") return null;
  const peso = Number(value);
  if (Number.isNaN(peso)) return String(value);
  return `${peso.toLocaleString("pt-BR", {
    minimumFractionDigits: peso % 1 === 0 ? 0 : 4,
    maximumFractionDigits: 4,
  })} kg`;
}

export function formatDateTimeLabel(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("pt-BR");
}

export function formatPresetPeriodLabel(periodoSugerido) {
  if (!periodoSugerido?.de || !periodoSugerido?.ate) return null;
  return `${formatDateLabel(periodoSugerido.de)} ate ${formatDateLabel(periodoSugerido.ate)}`;
}

export function buildCriteria(items) {
  return items.filter((item) => item.value != null && item.value !== "");
}

export function buildRecalculoRequest(params) {
  const {
    tipo,
    empresaId,
    transportadoraId,
    arquivoChaves,
    statusCalculo,
    dataEmissaoNFeDe,
    dataEmissaoNFeAte,
    chaveNFe,
    cnpjDestinatario,
    idEmbarcadorRemetente,
    idNegocio,
    idEstadoDestino,
    idCidadeDestino,
    situacoes,
    somentePendentesCalculo,
    dataEmissaoCTeDe,
    dataEmissaoCTeAte,
    numeroCte,
    chaveAcessoCte,
  } = params;

  if (!empresaId) return null;

  const statusCalculoExpandido = Array.isArray(statusCalculo) && statusCalculo.length
    ? Array.from(
        new Set(
          STATUS_CALCULO_OPTIONS
            .filter((opt) => statusCalculo.includes(opt.value))
            .flatMap((opt) => opt.statuses)
        )
      )
    : Array.from(new Set(STATUS_CALCULO_OPTIONS.flatMap((opt) => opt.statuses)));

  if (tipo === TIPO_ARQUIVO) {
    return {
      endpoint: "/recalculo/nfe/por-chaves",
      previewEndpoint: "/recalculo/nfe/por-chaves/preview",
      body: { embarcadorId: empresaId, chaves: arquivoChaves },
      canExecute: Array.isArray(arquivoChaves) && arquivoChaves.length > 0,
    };
  }

  if (tipo === TIPO_NF) {
    return {
      endpoint: "/recalculo/nfe",
      previewEndpoint: "/recalculo/nfe/preview",
      body: {
        embarcadorId: empresaId,
        transportadoraIds: transportadoraId ? [transportadoraId] : undefined,
        statusCalculo: statusCalculoExpandido,
        dataEmissaoNFeDe: dataEmissaoNFeDe || undefined,
        dataEmissaoNFeAte: dataEmissaoNFeAte || undefined,
        chaveNFe: chaveNFe || undefined,
        cnpjDestinatario: cnpjDestinatario || undefined,
        idEmbarcadorRemetente: idEmbarcadorRemetente || undefined,
        idNegocio: idNegocio ? Number(idNegocio) : undefined,
        idEstadoDestino: idEstadoDestino ? Number(idEstadoDestino) : undefined,
        idCidadeDestino: idCidadeDestino ? Number(idCidadeDestino) : undefined,
      },
      canExecute: true,
    };
  }

  return {
    endpoint: "/recalculo/cte",
    previewEndpoint: "/recalculo/cte/preview",
    body: {
      embarcadorId: empresaId,
      transportadoraIds: transportadoraId ? [transportadoraId] : undefined,
      situacoes,
      somentePendentesCalculo: Boolean(somentePendentesCalculo),
      dataEmissaoCTeDe: dataEmissaoCTeDe || undefined,
      dataEmissaoCTeAte: dataEmissaoCTeAte || undefined,
      numeroCte: numeroCte || undefined,
      chaveCTe: chaveAcessoCte || undefined,
    },
    canExecute: true,
  };
}

/**
 * Converte a resposta de POST /recalculo/nfe|cte|nfe/por-chaves nas linhas da
 * tabela (mesma regra de status de antes: erro, pendente quando o frete com
 * imposto voltou zerado, calculado). Os campos *Num guardam o número cru para
 * ordenar e exportar sem reconverter o texto formatado.
 */
export function mapearResultados(resultados, formatar) {
  return (resultados || []).map((r, idx) => {
    const pendente = r.sucesso && r.totais && r.totais.freteComImposto === 0;
    const status = !r.sucesso ? "Erro" : pendente ? "Pendente" : "Calculado";
    const erro = r.erro || "";
    const valorSimuladoNum = r.totais ? r.totais.freteComImposto : null;
    const valorCobradoNum = r.divergencia ? r.divergencia.freteReceberInformado : null;
    const diferencaNum = r.divergencia && r.divergencia.diferenca != null ? r.divergencia.diferenca : null;
    return {
      id: idx + 1,
      idx: idx + 1,
      transportadora: r.transportadora || "-",
      idCTeValue: r.idCTe || null,
      idTransportadoraValue: r.idTransportadora || null,
      numeroCTe: r.numeroCTe || r.idCTe || "-",
      modelo: r.modelo != null ? r.modelo : null,
      numerosNFs: Array.isArray(r.numerosNotasFiscais) && r.numerosNotasFiscais.length
        ? r.numerosNotasFiscais.join(", ")
        : Array.isArray(r.notasFiscais) ? r.notasFiscais.join(", ") : "-",
      valorSimulado: r.totais ? formatar(r.totais.freteComImposto) : "-",
      valorCobrado: r.divergencia ? formatar(r.divergencia.freteReceberInformado) : "-",
      diferenca: diferencaNum != null ? formatar(diferencaNum) : "-",
      valorSimuladoNum,
      valorCobradoNum,
      diferencaNum,
      status,
      erro,
      _log: r.log || null,
    };
  });
}

export function totaisDosResultados(rows, totalApi) {
  return {
    total: totalApi != null ? totalApi : rows.length,
    calculados: rows.filter((r) => r.status === "Calculado").length,
    pendentes: rows.filter((r) => r.status === "Pendente").length,
    comErro: rows.filter((r) => r.status === "Erro").length,
  };
}

export const ASYNC_STATUS_FINAIS = ["completed", "completed_with_errors", "completed_with_business_errors"];

export function rotuloStatusAsync(status) {
  if (status === "queued") return "Aguardando início";
  if (status === "running") return "Processando";
  if (status === "completed") return "Concluído";
  if (status === "completed_with_business_errors") return "Concluído com pendências";
  return "Concluído com erros";
}
